/*
 * Copyright (c) 2021. American Express Company
 */

import { filter, find } from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';

// models and services
import { PersonDialogViewModel } from 'src/types/view-models/person-dialog.view.model';
import MixpanelService from '../../services/mixpanel.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { PersonModel, RelationshipType } from 'src/types/person.model';
import { FullUserModel } from 'src/types/user.model';

// components
import PersonService from '../../services/person.service';
import { ButtonPrimary, ButtonSecondary, ButtonTertiary, SelectOption } from '@americanexpress/dls-react';
import Typography from '../typography/Typography';
import Dropdown from '../formik-fields/dropdown/Dropdown';
import BasicInput from '../formik-fields/basic-input/BasicInput';
import DateInput from '../formik-fields/date-input/DateInput';

// stylesheets
import styles from './PersonDialog.module.scss';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import parentStyles from 'src/bw/bw-drawer/financial-profile-drawer/FinancialProfileDrawer.module.scss';
import Selectors from 'src/utils/selectors';

interface FormValues {
    nickname: string;
    relationship: RelationshipType;
    dob: LocalDate;
}

export default function PersonDialog({
    className,
    person,
    open,
    updateUserLocally,
    updatePerson,
    updatePersons,
    close,
    user
}: PersonDialogViewModel): JSX.Element {
    const [isImageEditing] = useState(false);

    const defaultDOB: LocalDate = LocalDate.now();

    // get CSS classes
    const getClasses = (): string => {
        const classes: string[] = [];

        if (className) {
            classes.push(className);
        }

        classes.push(styles.personDialog);

        return classes.join(' ');
    };

    const onSubmit = async (values: FormValues): Promise<void> => {
        const persons: PersonModel[] = await PersonService.getPersons();
        const thisPerson: FormValues & PersonModel = { ...person, ...values };
        if (thisPerson?.personUid) {
            await PersonService.updatePerson(thisPerson);

            if (thisPerson?.relationship === RelationshipType.Self) {
                const newUser: FullUserModel = user;
                newUser.persons = persons;
                newUser.currentPerson = find(newUser.persons, { relationship: RelationshipType.Self });
                updateUserLocally(newUser);
            }
        } else {
            await PersonService.createPerson(thisPerson);
            if (user.attributes.startupStep !== undefined) {
                const count: number = filter(persons, { relationship: thisPerson?.relationship }).length + 1;
                MixpanelService.track(`o4 add family member ${thisPerson?.relationship} ${count}`);
            }
        }
        await updatePersons();
    };

    return (
        <Dialog open={Boolean(open)} PaperProps={{ className: parentStyles.personPaper }} className={getClasses()}>
            <Formik
                initialValues={{
                    nickname: person?.nickname || '',
                    relationship: person?.relationship,
                    dob: person?.dob ? person?.dob : defaultDOB
                }}
                validationSchema={Yup.object({
                    nickname: Yup.string().required(LiteralsService.getLiteral('FormErrors.nameInputError')),
                    relationship: Yup.string().when('person?.relationship', {
                        is: RelationshipType.Self,
                        then: Yup.string(),
                        otherwise: Yup.string().required(LiteralsService.getLiteral('FormErrors.relationshipRequired'))
                    }),
                    dob: Yup.date()
                        .max(LocalDate.now(), LiteralsService.getLiteral('FormErrors.birthDatePast'))
                        .required(LiteralsService.getLiteral('FormErrors.birthDateRequired'))
                })}
                onSubmit={onSubmit}
            >
                <Form>
                    <div>
                        <Typography variant={TypographyVariants.HEADING_S} className="after24">
                            {person?.relationship === RelationshipType.Self
                                ? LiteralsService.getLiteral('PersonDialog.planOwner')
                                : `${
                                      person?.personUid
                                          ? LiteralsService.getLiteral('CommonText.edit')
                                          : LiteralsService.getLiteral('CommonText.add')
                                  } ${LiteralsService.getLiteral('PersonDialog.familyMember')}`}
                        </Typography>
                        <BasicInput id="nickname" label={LiteralsService.getLiteral('CommonLabels.name')} />

                        {person?.relationship === RelationshipType.Self ? null : (
                            <Dropdown
                                id="relationship"
                                label="Relationship"
                                defaultSelectOption={
                                    <SelectOption selected disabled value="">
                                        {LiteralsService.getLiteral('PersonDialog.chooseRelationship')}
                                    </SelectOption>
                                }
                            >
                                <SelectOption value={RelationshipType.Partner}>
                                    {LiteralsService.getLiteral('PersonDialog.partner')}
                                </SelectOption>
                                <SelectOption value={RelationshipType.Spouse}>
                                    {LiteralsService.getLiteral('PersonDialog.spouse')}
                                </SelectOption>
                                <SelectOption value={RelationshipType.Child}>
                                    {LiteralsService.getLiteral('PersonDialog.child')}
                                </SelectOption>
                                <SelectOption value={RelationshipType.Parent}>
                                    {LiteralsService.getLiteral('PersonDialog.parent')}
                                </SelectOption>
                                <SelectOption value={RelationshipType.Sibling}>
                                    {LiteralsService.getLiteral('PersonDialog.sibling')}
                                </SelectOption>
                                <SelectOption value={RelationshipType.Other}>
                                    {LiteralsService.getLiteral('PersonDialog.other')}
                                </SelectOption>
                            </Dropdown>
                        )}

                        <div className={styles.dobPicker}>
                            <DateInput id="dob" label={LiteralsService.getLiteral('PersonDialog.dob')} showDay />
                        </div>
                    </div>
                    {/* <ImageUpload
                 imagePreview={(person?.photo &&
                 URL.createObjectURL(person?.photo)) ||
                 (person?.photos?.length &&
                 (
                 (person?.photos[0].absoluteUrl &&
                 `${person?.photos[0].absoluteUrl}`) ||
                 (person?.photos[0].relativeUrl &&
                 `${BW_PHANT_URL}/${person?.photos[0].relativeUrl}`)
                 ))}
                 onCancel={() => setState({ isImageEditing: false })}
                 onImageLoaded={() => setState({ isImageEditing: true })}
                 onSubmit={(blob: Blob) => {
                 if (user.attributes.startupStep !== undefined) {
                 MixpanelService.track('o4 Photo Upload: ' + person?.relationship);
                 }
                 updatePerson('photo', blob);
                 setState({ isImageEditing: false });
                 }}
                 roundedPreview={true}
                 /> */}
                    <div>
                        {!isImageEditing && (
                            <div className={parentStyles.buttons}>
                                <ButtonPrimary type="submit" data-cy="addEditButton">
                                    {person?.personUid
                                        ? LiteralsService.getLiteral('CommonText.update')
                                        : LiteralsService.getLiteral('CommonText.add')}
                                </ButtonPrimary>
                                <ButtonSecondary onClick={() => close()}>
                                    {LiteralsService.getLiteral('CommonText.cancel')}
                                </ButtonSecondary>
                                {person?.personUid && person?.relationship !== RelationshipType.Self ? (
                                    <ButtonTertiary
                                        className="right"
                                        data-cy={Selectors.PlanAhead.REMOVE_BUTTON}
                                        onClick={async () => {
                                            await PersonService.deletePerson(person);
                                            await updatePersons();
                                        }}
                                    >
                                        {LiteralsService.getLiteral('CommonText.remove')}
                                    </ButtonTertiary>
                                ) : null}
                            </div>
                        )}
                    </div>
                </Form>
            </Formik>
        </Dialog>
    );
}
